import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccountSwitcher = ({ accounts, selectedAccount, onAccountChange, onTransfer }) => {
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [transferData, setTransferData] = useState({
    fromAccount: '',
    toAccount: '',
    amount: '',
    currency: 'USD'
  });
  const [createAccountData, setCreateAccountData] = useState({
    password: '',
    accountType: '',
    confirmPassword: ''
  });

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const handleTransferSubmit = (e) => {
    e?.preventDefault();
    onTransfer(transferData);
    setShowTransferModal(false);
    setTransferData({
      fromAccount: '',
      toAccount: '',
      amount: '',
      currency: 'USD'
    });
  };

  const handleCreateAccountSubmit = (e) => {
    e?.preventDefault();
    // Validate password confirmation
    if (createAccountData?.password !== createAccountData?.confirmPassword) {
      alert('Parolalar eşleşmiyor!');
      return;
    }
    console.log('Creating new account:', createAccountData);
    // In a real app, this would make an API call to create the account
    setShowCreateAccountModal(false);
    setCreateAccountData({
      password: '',
      accountType: '',
      confirmPassword: ''
    });
  };

  const getAccountStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'restricted': return 'text-amber-600 bg-amber-50';
      case 'suspended': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const accountTypes = [
    { value: 'live', label: 'Canlı Hesap' },
    { value: 'demo', label: 'Demo Hesap' },
    { value: 'practice', label: 'Pratik Hesap' },
    { value: 'islamic', label: 'İslami Hesap' }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Account Overview</h2>
          <Button
            variant="outline"
            size="sm"
            iconName="ArrowLeftRight"
            iconPosition="left"
            onClick={() => setShowTransferModal(true)}
          >
            Transfer Funds
          </Button>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts?.map((account) => (
            <div
              key={account?.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                selectedAccount?.id === account?.id
                  ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => onAccountChange(account)}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium text-gray-900">{account?.name}</h3>
                  <p className="text-sm text-gray-500">#{account?.accountNumber}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getAccountStatusColor(account?.status)}`}>
                  {account?.status}
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Balance:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(account?.balance, account?.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Equity:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(account?.equity, account?.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Free Margin:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(account?.freeMargin, account?.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Credit:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(account?.credit, account?.currency)}
                  </span>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center text-xs text-gray-500">
                  <Icon name="Clock" size={12} className="mr-1" />
                  Last updated: {new Date(account.lastUpdated)?.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="mt-6 mb-4">
          <hr className="border-gray-300" />
        </div>

        {/* Create Account Button */}
        <div className="flex justify-center">
          <Button
            variant="default"
            size="md"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateAccountModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Hesap Oluştur
          </Button>
        </div>
      </div>
      
      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Transfer Funds</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowTransferModal(false)}
                />
              </div>
            </div>

            <form onSubmit={handleTransferSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Account
                  </label>
                  <select
                    value={transferData?.fromAccount}
                    onChange={(e) => setTransferData({...transferData, fromAccount: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select account</option>
                    {accounts?.map((account) => (
                      <option key={account?.id} value={account?.id}>
                        {account?.name} - {formatCurrency(account?.balance, account?.currency)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Account
                  </label>
                  <select
                    value={transferData?.toAccount}
                    onChange={(e) => setTransferData({...transferData, toAccount: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select account</option>
                    {accounts?.map((account) => (
                      <option key={account?.id} value={account?.id}>
                        {account?.name} - {formatCurrency(account?.balance, account?.currency)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={transferData?.amount}
                    onChange={(e) => setTransferData({...transferData, amount: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter amount"
                    required
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTransferModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="flex-1"
                >
                  Transfer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateAccountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Yeni Hesap Oluştur</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => setShowCreateAccountModal(false)}
                />
              </div>
            </div>

            <form onSubmit={handleCreateAccountSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hesap Türü
                  </label>
                  <select
                    value={createAccountData?.accountType}
                    onChange={(e) => setCreateAccountData({...createAccountData, accountType: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Hesap türünü seçin</option>
                    {accountTypes?.map((type) => (
                      <option key={type?.value} value={type?.value}>
                        {type?.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parola
                  </label>
                  <input
                    type="password"
                    value={createAccountData?.password}
                    onChange={(e) => setCreateAccountData({...createAccountData, password: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Hesap parolasını girin"
                    required
                    minLength="8"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Parola Onayı
                  </label>
                  <input
                    type="password"
                    value={createAccountData?.confirmPassword}
                    onChange={(e) => setCreateAccountData({...createAccountData, confirmPassword: e?.target?.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Parolayı tekrar girin"
                    required
                    minLength="8"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAccountModal(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Hesap Oluştur
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;